import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import useConversationContext from '../hooks/useConversationContext';
import UserProfile from './UserProfile';
import useTitle from '../hooks/useTitle';
import { sendDigestTest } from '../api';
import './SettingsPage.css';

// The weekly note by email. The flag lives on the user document, which the
// backend's Monday job reads; nothing else on this page touches it.
function DigestCard({ user }) {
  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  useEffect(() => {
    if (!user?.uid) return;
    getDoc(doc(db, 'users', user.uid))
      .then((snap) => setEnabled(Boolean(snap.data()?.digestEnabled)))
      .catch(() => setEnabled(false));
  }, [user?.uid]);

  const toggle = async (next) => {
    setEnabled(next);
    try {
      await setDoc(doc(db, 'users', user.uid), { digestEnabled: next, digestUpdatedAt: new Date() }, { merge: true });
      setNote(next ? `On. Mondays, to ${user.email}.` : 'Off.');
    } catch (e) {
      setEnabled(!next);
      setNote('Could not save that. Try again.');
    }
  };

  const sendNow = async () => {
    setBusy(true);
    setNote(null);
    try {
      const res = await sendDigestTest();
      setNote(`Sent to ${res.to}. Check your inbox (and spam, the first time).`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setNote((typeof detail === 'string' ? detail : detail?.message) || 'Could not send it right now.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sp-card">
      <div className="sp-card-head">
        <div>
          <h2 className="dd-serif">Weekly note by email</h2>
          <p className="dd-muted">Every Monday: your total, the week's move, the biggest movers, earnings ahead and the briefing. One email, no marketing.</p>
        </div>
        <label className="sp-switch">
          <input type="checkbox" checked={Boolean(enabled)} disabled={enabled === null} onChange={(e) => toggle(e.target.checked)} />
          <span className="sp-switch-track" aria-hidden="true" />
          <span className="sp-switch-label">{enabled ? 'On' : 'Off'}</span>
        </label>
      </div>
      <div className="sp-card-foot">
        <button type="button" className="dd-btn-ghost" onClick={sendNow} disabled={busy}>{busy ? 'Sending…' : 'Send me one now'}</button>
        {note && <span className="dd-mono dd-muted">{note}</span>}
      </div>
    </section>
  );
}

// Holdings and profile as a page instead of a modal copied into every screen.
export default function SettingsPage({ user }) {
  const { userProfile, updateUserProfile, loading } = useConversationContext(user);
  useTitle('Holdings & profile');

  return (
    <div className="dd-page">
      <div className="dd-page-head">
        <h1>Holdings &amp; profile</h1>
        <p>What you hold, in which currency, and how you invest. Everything on the portfolio page is computed from this.</p>
      </div>
      {loading ? (
        <p className="dd-muted">Loading your profile…</p>
      ) : (
        <UserProfile inline userProfile={userProfile} onUpdateProfile={updateUserProfile} />
      )}
      <DigestCard user={user} />
    </div>
  );
}
